import { Timestamp } from 'firebase/firestore';

// Domain Models
export type MuseTier = 1 | 2 | 3; // 1 = normal, 2 = shiny, 3 = chrome

export interface Muse {
  name: string;
  img: string;
  rarity: 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary';
  emoji?: string;
  weight?: number;
  pack?: string;
  tier?: MuseTier; // Upgrade tier: 1 (normal), 2 (shiny), 3 (chrome)
}

// Muse Upgrade Constants
export const MUSE_UPGRADE_COSTS = {
  TIER_2: 1, // 1 star to upgrade to shiny
  TIER_3: 2  // 2 stars to upgrade to chrome (from shiny)
} as const;

export interface Pack {
  name: string;
  price: number;
  description: string;
  muses: Muse[];
  dropRates: number[];
  // Optional seasonal metadata for timed releases
  releaseAt?: string; // ISO string for when the pack unlocks
  endAt?: string;     // ISO string for when the season ends
  seasonalGroup?: string; // e.g., "Fall 2025"
}

/**
 * Generic uploaded file metadata stored in Firestore.
 * Used for teacher trainings/certifications and admin-managed clearances.
 */
export type UploadedFileMeta = {
  storagePath: string;
  url: string;
  filename?: string;
  contentType?: string;
  sizeBytes?: number;
  uploadedAt?: Timestamp | Date | null;
  uploadedBy?: string | null;
};

export type TeacherBgCheckType = 'cori' | 'fingerprint' | 'tb_test' | 'other';

export type TeacherSchoolClearanceItem = {
  id: string;
  type: TeacherBgCheckType;
  /**
   * Used when type === 'other' (free-form label).
   */
  typeLabel?: string | null;
  file: UploadedFileMeta;
  uploadedAt?: Timestamp | Date | null;
  uploadedBy?: string | null;
};

export type TeacherFirstClassObserved = {
  /**
   * YYYY-MM-DD (simple to edit in admin UI).
   */
  date?: string | null;
  observer?: string | null;
};

export type ShippingAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null; // e.g. 'IL'
  zip?: string | null;
  country?: string | null;
  /**
   * Google Places placeId if selected from autocomplete.
   * Helps auto-fill and validate address components.
   */
  placeId?: string | null;
  /**
   * Optional formatted address from the provider.
   */
  formattedAddress?: string | null;
};

/**
 * Represents a user in the system, compatible with Firebase Auth and Firestore.
 */
export interface User {
  id: string; // UID from Firebase Auth
  dashboardWallpaper?: { preset: string; imageUrl?: string };
  displayName: string | null;
  email: string | null;
  /**
   * Email used to look up the teacher's Slack account.
   * Stored separately from the auth/account email.
   */
  slackEmail?: string | null;
  role: 'teacher' | 'student' | 'parent' | 'partner';
  isAdmin?: boolean; // Administrative privileges (can be combined with any role)
  /** Scoped admin workspace access. Full admins implicitly have every permission. */
  adminPermissions?: Array<'curriculum.manage' | 'certifications.manage'>;
  /**
   * Staff lifecycle state for teachers.
   * - active/sub: normal access
   * - applicant/inactive/offboarded: extremely restricted access (no squads/calendar/child data)
   *
   * Defaults to 'active' when missing.
   */
  staffStatus?: 'applicant' | 'active' | 'sub' | 'inactive' | 'offboarded';
  /** Limited dashboard access while the state-specific background check awaits approval. */
  onboardingAccess?: 'home_schedule';
  selectedMuse: Muse | null;
  /** Teacher-selected avatar used on the classroom dashboard. */
  classroomAvatarUrl?: string;
  classroomAvatarKind?: 'muse' | 'upload';
  museCash?: number;
  stars?: number;
  muses?: Muse[];
  // Squad + level tracking
  ogLevel?: number;            // Overture level: number of squads the student is in (1:1 for now)
  squadIds?: string[];         // All squad IDs this user (student) belongs to
  // Tag system (profile tags / titles)
  ownedTagIds?: string[];      // All tagDefinition IDs the user owns
  equippedTagId?: string | null; // Currently equipped tag ID (if any)
  favoriteSquadIds?: string[];
  createdAt?: Timestamp | Date;
  parentEmail?: string;
  totalSongsCreated?: number;
  totalTimePlayed?: number; // Total time played in milliseconds (like Roblox/Fortnite)
  lastLogin?: Timestamp;
  /**
   * Login counter (primarily used for partner accounts).
   * Incremented on successful login.
   */
  loginCount?: number;
  currentSquadId?: string;
  
  // Teacher-specific fields
  phoneNumber?: string;
  /** 24-hour reminders for classes assigned to this teacher. Missing values default to on. */
  classReminderPreferences?: {
    email?: boolean;
    sms?: boolean;
  };
  neighborhood?: string;
  /**
   * Shipping address used for sending Overture merch/devices.
   * Kept separate from public profile fields.
   */
  shippingAddress?: ShippingAddress | null;
  /**
   * Admin-only cached map point derived from the teacher's shipping address.
   * It is invalidated whenever the saved address fingerprint changes and is
   * never copied to publicTeachers.
   */
  teacherMapLocation?: {
    latitude: number;
    longitude: number;
    addressFingerprint: string;
    placeId?: string | null;
    geocodedAt?: Timestamp | Date | null;
  } | null;
  /**
   * Gusto employee id used for payroll exports/import.
   * Stored on the teacher user doc so Admin can edit in Teachers table.
   */
  gustoEmployeeId?: string | null;
  /**
   * Short teacher bio shown in teacher profiles.
   * Target max length is 100 words (enforced in UI).
   */
  bio?: string | null;
  /**
   * Public URL to teacher's headshot photo (auto-synced from Storage).
   * Updated by Cloud Function when teacher uploads to /teachers/{id}/headshot.{ext}
   */
  headshotUrl?: string | null;
  /**
   * Timestamp when headshot was last updated.
   */
  headshotUpdatedAt?: Timestamp | Date | null;
  /**
   * When the teacher has "music" selected as a subject, this indicates whether they teach private lessons.
   */
  musicPrivateLessons?: boolean;
  /**
   * When `musicPrivateLessons` is true, the instruments / concepts they teach (multi-select).
   */
  musicTopics?: string[];
  /**
   * @deprecated Use `neighborhood` instead.
   * Kept temporarily for backward-compat with older teacher records.
   */
  address?: string;
  hasCar?: boolean;
  hasDriversLicense?: boolean;
  deviceCount?: number;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  timeZone?: string;
  /**
   * US state where the teacher is located (e.g., 'IL', 'CA').
   * Used for public profile display.
   */
  state?: string;
  startDate?: string; // ISO date string
  teacherType?: 'lead' | 'partner';
  subjects?: string[]; // e.g., ['AI', 'Music', 'Art']
  backgroundCheckCleared?: boolean;
  /** Admin-confirmed Massachusetts CORI background check. */
  coriCleared?: boolean;
  hourlyRate?: number; // Compensation rate per hour in dollars
  /**
   * Whether this teacher's profile should be visible on the public marketing website.
   * Admin-controlled field. When true, teacher data is synced to publicTeachers collection.
   */
  isPublic?: boolean;
  /**
   * LinkedIn profile URL for the teacher.
   */
  linkedinUrl?: string;

  // ----------------------
  // Teacher compliance / onboarding (stored in /teachers/{teacherId})
  // These are merged into the admin teacher table for convenience.
  // ----------------------
  overtureBgCheckFile?: UploadedFileMeta | null;
  mandatoryReportingCertificationFile?: UploadedFileMeta | null;
  tbTestFile?: UploadedFileMeta | null;
  signedContracts?: UploadedFileMeta[]; // array of files
  cycCleared?: boolean;
  ogTrainingComplete?: boolean;
  firstClassObserved?: TeacherFirstClassObserved | null;
  
  // Parent-specific fields
  studentIds?: string[];           // Array of child user IDs
  locationId?: string;
  locationSlug?: string;
  partnerId?: string;
  legacySchoolId?: string;
  /** Temporary heterogeneous legacy fallback. Never use for new writes. */
  schoolSlug?: string;
  phones?: string[];
  addresses?: object[];
  consents?: {
    tos?: ConsentRecord;
    photoRelease?: ConsentRecord;
  };
  status?: 'active' | 'invited' | 'suspended';
  
  // Student-specific fields  
  parentIds?: string[];            // Array of parent user IDs

  /**
   * Student pickup preference as captured during child creation / registration.
   * Shape matches admin + registration UI: { options: string[], otherText?: string }.
   */
  pickupPreference?: { options: string[]; otherText?: string } | null;
  /**
   * Teacher-visible dismissal assignment used in squad attendance views.
   * When unset, UI falls back to `pickupPreference` from linked parent settings.
   */
  dismissalProcedureType?: 'pickup' | 'aftercare' | 'walker' | 'special' | 'unassigned' | null;
  dismissalProcedureNote?: string | null;

  /**
   * Teacher quarterly weekly availability templates.
   * Stored on the teacher user doc under `availabilityTemplates`.
   */
  availabilityTemplates?: TeacherAvailabilityTemplates;

  /**
   * Date-specific availability overrides per quarter.
   * Stored on the teacher user doc under `availabilityExceptions`.
   * Keyed by quarterKey -> YYYY-MM-DD -> exception payload.
   */
  availabilityExceptions?: TeacherAvailabilityExceptions;
}

export type AvailabilityQuarterKey =
  | 'winter2026'
  | 'spring2026'
  | 'summer2026'
  | 'fall2026'
  | 'winter2027'
  | 'spring2027';
export type AvailabilityStatus = 'available' | 'maybe' | 'unavailable';

export type AvailabilityMaybeReason =
  | 'schedule_conflict'
  | 'travel'
  | 'transportation'
  | 'health'
  | 'other';

export interface AvailabilityTimeBlock {
  start: string; // "HH:mm"
  end: string;   // "HH:mm"
  status: AvailabilityStatus;
}

export interface AvailabilityDayTemplate {
  status: AvailabilityStatus;
  /**
   * Required when status is "maybe" (or when any time block is "maybe")
   */
  maybeReason?: AvailabilityMaybeReason | null;
  maybeNote?: string;
  /**
   * Optional fine-grained override within the day's standard window.
   */
  timeDetailsEnabled?: boolean;
  blocks?: AvailabilityTimeBlock[];
}

export interface AvailabilityWeekTemplate {
  sun: AvailabilityDayTemplate;
  mon: AvailabilityDayTemplate;
  tue: AvailabilityDayTemplate;
  wed: AvailabilityDayTemplate;
  thu: AvailabilityDayTemplate;
  fri: AvailabilityDayTemplate;
  sat: AvailabilityDayTemplate;
}

export interface TeacherAvailabilityQuarterTemplate {
  quarterKey: AvailabilityQuarterKey;
  week: AvailabilityWeekTemplate;
  updatedAt?: string; // ISO string
}

export type TeacherAvailabilityTemplates = Partial<Record<AvailabilityQuarterKey, TeacherAvailabilityQuarterTemplate>>;

export type AvailabilityExceptionType = 'unavailable';
export type AvailabilityExceptionTimeMode = 'all_day' | 'window' | 'custom';

export interface TeacherAvailabilityException {
  type: AvailabilityExceptionType; // currently only "unavailable"
  timeMode: AvailabilityExceptionTimeMode;
  /**
   * Only used when timeMode = "custom".
   * Interpreted in CT when performing conflict checks and display.
   */
  start?: string; // "HH:mm"
  end?: string;   // "HH:mm"
  note?: string;
  updatedAt?: string; // ISO string
}

export type TeacherAvailabilityExceptions =
  Partial<Record<AvailabilityQuarterKey, Record<string, TeacherAvailabilityException>>>;

export interface Game {
  title: string;
  img: string | null;
  path: string;
  desc: string;
}

// Tag (profile title) definitions used for Muse Vault tag packs
export interface TagDefinition {
  id: string;
  name: string;
  label: string; // e.g., "Level 3 Tag", "Leafy Lands Tag"
  unlockType: 'level' | 'season';
  requiredOgLevel?: number; // for level-based tags
  seasonKey?: string;       // for season-based tags (e.g., 'leafy-lands')
  priceMuseCash: number;
  styleKey: string;         // controls animated text style in UI
  isActive: boolean;
  sortOrder?: number;
}

export interface Level {
  level: number;
  title: string;
  game: string;
  img: string | null;
  cash: number;
}

export interface Location {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

// Component Props
export interface MuseCardProps {
  img?: string;
  name?: string;
  emoji?: string;
  rarity?: string;
  count?: number;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  locked?: boolean;
  pack?: string | null;
}

export interface StatsPropsData {
  user: User | null;
  muse: Muse | null;
  rarityCounts: RarityCounts;
  museCash: number;
}

export interface RarityCounts {
  Common: number;
  Uncommon: number;
  Rare: number;
  Epic: number;
  Legendary: number;
}

export interface ComponentWithMuseSelect {
  onMuseSelect?: (muse: Muse) => void;
}

export interface MyMusesProps extends ComponentWithMuseSelect {}
export interface PickMuseProps extends ComponentWithMuseSelect {}

// Firebase/Database types
// No longer needed, User interface is now the standard
/*
export interface UserData {
  role: 'teacher' | 'student';
  email: string | null;
  displayName: string;
  museCash: number;
  muses: Muse[];
  selectedMuse: Muse | null;
  createdAt: any;
}
*/

export interface PackPurchaseModal {
  packId: string;
  pack: Pack;
}

// Utility types
export type Rarity = 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary';

export interface LayoutProps {
  children: React.ReactElement<ComponentWithMuseSelect>;
}

export interface ProtectedRouteProps {
  children: React.ReactElement;
}

/**
 * Represents a Squad, which is a group of students led by a teacher.
 */
export interface Squad {
  id: string;
  dashboardWallpaper?: { preset: string; imageUrl?: string };
  classroomAvatarUrl?: string;
  classroomAvatarKind?: 'muse' | 'upload';
  code: string;
  title: string;
  studentIds: string[];
  teacherIds: string[];
  leadTeacherId?: string; // New: designated lead teacher for the squad
  /**
   * Whether this squad is run in-person or online.
   * Stored on the squad doc so Ops Flows can track it as a logistic.
   */
  deliveryMode?: 'in_person' | 'online';
  plannedTeacherAvailabilityConfirmed?: boolean;
  plannedTeacherOvertureTrainingCompleted?: boolean;
  delayProgramAWeekAllowed?: boolean;
  techTestedOnSite?: boolean;
  enrolledKidNames?: string[];
  enrolledParentNamesAndEmails?: string[];
  halfway?: {
    engagementScore?: number; // 1–5
    attendanceTrend?: 'up' | 'flat' | 'down';
    pacingStatus?: 'behind' | 'on_track' | 'ahead';
    teacherPerformanceScore?: number; // 1–5
    techIssueStatus?: 'none' | 'minor' | 'blocking';
    schoolSatisfactionScore?: number; // 1–5
    renewalLikelihood?: 'yes' | 'maybe' | 'no';
  };
  createdAt: Timestamp;
  notes?: string;
  onlineStudentIds?: string[];
  location?: Location;
  
  // Curriculum configuration
  curriculumId?: string; // Phase 1: defaults to 'music' when missing
  /**
   * Games that are currently unlocked for this squad while a class is live.
   * - When no liveSession is present, student collections ignore this and default to all games.
   * - When a liveSession is active, student collections use this (per-squad) list to determine access.
   */
  unlockedGames?: string[];
  
  // Curriculum Progression
  currentWorldId?: string;
  currentWorldTitle?: string;
  currentLessonLevel?: number; // 1-based level within the world
  currentLessonId?: string;
  currentGoal?: string;
  classStatus?: 'next' | 'current'; // Whether this is the next class or currently in session
  completedLevels?: string[]; // Array of level IDs that have been completed
  
  // Attendance Tracking
  attendance?: Record<string, boolean>; // studentId -> active/inactive
  lastAttendanceUpdate?: Date;
  attendanceMetadata?: {
    lastTaken: string; // ISO date string
    takenBy: string; // teacher's user ID
  };
  liveSession?: {
    teacherId: string;
    teacherName: string;
    startTime: Timestamp;
    sessionId: string;
    location?: {
      latitude: number;
      longitude: number;
      accuracy: number;
      timestamp: Date;
      error?: string;
    };
  };
  
  // Active game launch session (teacher pushes game to all students)
  activeLaunchSession?: {
    gameId: string;
    teacherId: string;
    teacherName: string;
    sessionId: string;
    version: number;
    createdAt: Timestamp;
  };
  
  // Mission Progress Tracking
  missionProgress?: Record<string, Record<string, boolean | number>>; // levelId -> missionId -> progress
  lastMissionUpdate?: Date;
  
  // Logistics Information
  regularTeacher?: string;
  arrivalTime?: string;
  classTime?: string;
  wifiUsername?: string;
  wifiPassword?: string;
  pocOnSite?: string;
  pocContact?: string;
  /**
   * New (preferred) breakdown of on-site procedures.
   * These mirror similarly-named fields on the School doc and can be refined by teachers after visits.
   */
  arrivalProcedure?: string;
  dismissalProcedure?: string;
  lateArrivalProcedure?: string;
  hallwayRules?: string;
  parkingInstructions?: string;
  /**
   * Legacy combined field (kept for backwards-compat).
   */
  arrivalDismissalProcedure?: string;
  address?: string;
  classroomName?: string;
  materialsOnSite?: string;
  startGrade?: string;
  endGrade?: string;
  bathroomRules?: string;
  otherImportantRules?: string;
  additionalNotes?: string;
  /**
   * When a teacher truly doesn't know a logistics answer yet, they can mark it explicitly as unknown.
   * This is used to avoid blocking sub requests while still signaling the substitute they must confirm on-site.
   *
   * Keyed by teacher-editable logistics field name.
   */
  teacherLogisticsUnknown?: Record<string, boolean>;
  locationName?: string;
  /**
   * Canonical organization/location hierarchy.
   * During migration, `schoolId` remains as read-only provenance until legacy retirement.
   */
  partnerId?: string;
  locationId?: string;
  seasonId?: string;
  seasonAssignmentSource?: 'creation_context' | 'inferred_from_start_date' | 'manual_override';
  seasonAssignmentReason?: string;
  schoolId?: string;
  schoolName?: string;
  
  // Timezone configuration (IANA). Defaults to 'America/Chicago' if not set
  timezone?: string;
  
  // New fields for scheduling, pricing, etc.
  /**
   * Legacy price field (meaning depends on paymentMode).
   * Prefer using the explicit fields below.
   */
  priceCents?: number;
  /**
   * Tuition per student per hour (used for Stripe + enrollment-based school billing).
   */
  tuitionPerStudentPerClassCents?: number;
  /**
   * Flat fee per class session (used for flat-fee school billing).
   */
  flatFeePerClassCents?: number;
  currency?: string;
  /**
   * How this squad is paid for:
   * - stripe: paid online by parents (NOT invoiced to the school)
   * - school_flat_fee: invoiced as a flat fee for the squad/session
   * - school_enrollment: invoiced as (price per student) * (enrolled students)
   *
   * Back-compat: legacy value 'external' should be treated as 'school_enrollment'.
   */
  paymentMode?: 'stripe' | 'school_flat_fee' | 'school_enrollment' | 'external';
  /**
   * "Admin pay" payroll override for this squad.
   * When set (0 is allowed and meaningful), this hourly dollar rate replaces the
   * lead/main teacher's normal User.hourlyRate when payroll is calculated.
   * null/undefined = no override (normal teaching rate applies).
   */
  adminPayHourlyRate?: number | null;
  /**
   * Co-teachers assigned alongside the lead teacher (any number).
   * Each entry may carry its own admin-pay hourly override (dollars; 0 allowed)
   * that replaces that teacher's normal User.hourlyRate for this squad's classes.
   * null/undefined rate = that co-teacher is paid at their normal rate.
   */
  coTeachers?: Array<{ teacherId: string; adminPayHourlyRate?: number | null }>;
  /** Access/participation role for non-teaching staff assigned to this squad. */
  staffRolesByTeacherId?: Record<string, 'viewer' | 'content' | 'trainer' | 'class_support' | 'evaluator' | 'other'>;
  schedule?: {
    /** Single-day squads use mainDate instead of a recurring date range. */
    mainDate?: string;
    dayOfWeek?: string;
    startTime?: string;
    endTime?: string;
    startDate?: string;
    endDate?: string;
    exceptions?: string[];
    /** Partner-wide no-class days materialized from the partner calendar; managed by sync, not edited per-squad. */
    inheritedExceptions?: string[];
    makeups?: string[];
  };
  scheduleKind?: 'recurring' | 'single_day';
  singleDayType?: 'class' | 'demo';
  demoType?: 'event_booth' | 'lunchtime' | 'school_assembly' | 'free_workshop' | 'classroom_visit' | 'conference' | 'other';
  demoExperienceId?: string;
  /** School/event-specific flyer. Overrides the flyer configured on the demo experience. */
  demoFlyer?: {
    title: string;
    url: string;
    storagePath?: string;
    uploadedAt?: number;
    uploadedBy?: string;
  } | null;
  /** Explicit lifecycle for schedule trust; dates may exist before they are confirmed. */
  scheduleStatus?: 'draft' | 'proposed' | 'confirmed' | 'changed' | 'canceled';
  scheduleConfirmedAt?: Timestamp | number;
  scheduleConfirmedBy?: string;
  // Registration window (optional; used by ops/marketing)
  registrationStartDate?: string; // YYYY-MM-DD
  registrationEndDate?: string;   // YYYY-MM-DD
  /**
   * Approximate date range for the program when exact dates aren't finalized yet.
   * Example: "Sept 9 – Nov 18"
   */
  roughDateWindow?: string;
  /**
   * Commitment confirmation details (verbal/written + any context).
   * Example: "Written yes via email from principal on 2026-01-31"
   */
  commitmentConfirmation?: string;
  // Ops scheduling metadata
  scheduleConfirmByDate?: string; // YYYY-MM-DD
  // Enrollment / policy metadata
  proratedAllowed?: boolean;
  combineGradesAllowed?: boolean;
  minEnrollDropDeadDate?: string; // YYYY-MM-DD
  minEnrollOverrideBy?: string;
  revSplitPercent?: number;
  // Classroom / tech requirements
  hasProjectorOrTv?: boolean;
  projectorConnectionInstructions?: string;
  devicesOnSite?: boolean;
  // Contracting
  serviceAgreementSigned?: boolean;
  capacity?: number;
  minEnroll?: number;
  seatsTaken?: number;
  waitlistEnabled?: boolean;
  status?: 'lead' | 'published' | 'draft' | 'archived' | 'canceled';
  /**
   * Stable program term identifier (preferred).
   * Format: `${season}${year}` e.g. "winter2026".
   *
   * Backward compatibility:
   * - Legacy squads may only have `term` (string label).
   */
  termId?: string;
  term?: string;
  slug?: string;
  visibility?: 'public' | 'private';
  firstDate?: string;
  lastDate?: string;
  nextSessionAt?: string;
  seatsRemaining?: number;

  // Derivative written server-side (best-effort): total number of classes in the schedule
  totalClasses?: number;

  /**
   * Maps teaching days to curriculum levels for the classroom dashboard pager.
   * Length should equal teaching-day count (schedule / squadEvents).
   * Built client-side in Pass 1; persisted in Pass 2+.
   */
  sessionPlan?: {
    curriculumId: string;
    slots: Array<{
      index: number; // 1-based Class N of M
      levelId: string;
      /** Source lesson; levelId remains a unique progress key for this session. */
      curriculumId?: string;
      sourceLevelId?: string;
      eventId?: string;
      date?: string; // YYYY-MM-DD
    }>;
    updatedAt?: number;
  };

  // Whether this squad requires Overture-provided devices (iPads, etc.)
  requiresOvertureDevices?: boolean;

  /**
   * Doc-level edit metadata for the Squad Details page.
   * Used to build trust between teachers and admins.
   */
  detailsUpdatedAt?: number;
  detailsUpdatedBy?: string | null;
  teacherDetailsUpdatedAt?: number;
  teacherDetailsUpdatedBy?: string | null;
}

/**
 * Represents a real-time event that occurs within a squad.
 */
export interface SquadEvent {
  id: string;
  squadId: string;
  squadTitle: string;
  type: 'achievement' | 'song' | 'login';
  studentId: string;
  timestamp: Timestamp;
  payload: Record<string, any>; // e.g., { achievementId: '...', name: '...' }
}

/**
 * Represents the publicly available information for a student,
 * used in squad lists for login.
 */
export interface StudentPublicProfile {
  id: string;
  displayName: string;
  selectedMuse: Muse | null;
  email?: string;
}

/**
 * Represents a student session for anonymous authentication
 */
export interface StudentSession {
  studentId: string;
  squadId: string;
  loginTime: Timestamp;
  active: boolean;
}

/**
 * Represents a completed live teaching session with location data
 */
export interface LiveSession {
  id: string;
  sessionId: string;
  squadId: string;
  squadTitle: string;
  teacherId: string;
  teacherName: string;
  startTime: Timestamp;
  endTime: Timestamp;
  duration: number; // minutes
  location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
    timestamp: Date;
    error?: string;
    onSchoolProperty?: boolean; // To be determined by comparing with school boundaries
  };
  attendanceData?: Record<string, boolean>;
  /** Team membership captured when this lesson was finished. */
  teamRosterSnapshot?: Record<string, { name: string; memberIds: string[] }>;
  levelId?: string;
  levelTitle?: string;
  // Session lifecycle metadata
  status?: 'active' | 'completed' | 'canceled';
  canceledAt?: Timestamp | null;
  canceledByTeacherId?: string | null;
  cancelReason?: 'accidental' | 'otherTeacher' | 'preview' | 'other';
  cancelReasonOther?: string | null;
}

/**
 * Represents a teacher-led creative session (e.g., OG Create prompting) tied to a live squad session.
 */
export interface CreativeSession {
  id: string;
  squadId: string;
  teacherId: string;
  type: 'prompting';
  startTime: Timestamp;
  endTime?: Timestamp;
  status: 'active' | 'archived';
  missionText?: string;
  constraints?: string[];
}

/**
 * Represents a student's work within a creative session (per student per session).
 */
export interface CreativeStudentWork {
  studentId: string;
  squadId: string;
  sessionId: string;
  promptText: string;
  status: 'draft' | 'submitted' | 'ready' | 'needs_revision';
  teacherFeedback?: string;
  updatedAt: Timestamp;
}

export interface School {
  id: string;
  name: string;
  /**
   * Migration bridge to the canonical Partner → Location model.
   * Retained on legacy school docs until the website and all workflows cut over.
   */
  partnerId?: string;
  locationId?: string;
  slug?: string;
  address?: string;
  /**
   * Marketing flag: set true once we've run at least one squad at this school.
   * Maintained server-side from squad schedule dates.
   */
  hasWorkedWith?: boolean;
  hasWorkedWithUpdatedAt?: Timestamp | Date | null;
  /**
   * Organization type / school type for ops segmentation.
   */
  organizationType?:
    | 'private_school'
    | 'charter_school'
    | 'public_charter_school'
    | 'traditional_public'
    | 'public_magnet'
    | 'community_organization'
    | 'other';
  /**
   * Grade band served by the school (high-level banding for ops).
   */
  gradeLevelsOffered?: 'pre_k' | 'k_8' | 'elementary' | 'middle' | 'k_12' | 'high' | 'other';
  latitude?: number;
  longitude?: number;
  logo?: string;
  campusPhoto?: string;
  registrationLink?: string;
  wifiUsername?: string;
  wifiPassword?: string;
  wifiRules?: string;
  parkingInstructions?: string;
  sitesWhitelisted?: boolean;
  pocOnSite?: string;
  pocContactInfo?: string;
  pocContactId?: string;
  arrivalDismissalProcedure?: string;
  arrivalProcedure?: string;
  dismissalProcedure?: string;
  lateArrivalProcedure?: string;
  bathroomRules?: string;
  hallwayRules?: string;
  emergencyContactOnSite?: string;
  reliabilityScore?: number; // 1-5 (subjective)
  historyNotes?: string;
  safetyProtocolDocs?: string[]; // URLs (uploaded to Storage)
  additionalNotes?: string;
  attendanceSoftware?: string;
  firstDayInstructionsSent?: boolean;
  bgChecksAndSafetyTrainingsCompleted?: boolean;
  additionalPointsOfContact?: string[];
  /**
   * Early-stage screening questions for new locations.
   * Stored on the school doc under `screening.*` and referenced by ops logistics.
   */
  screening?: {
    hasPartnersLikeUs?: 'yes' | 'no' | 'unsure';
    partnerVendors?: string[]; // vendor/program names
    avgHouseholdIncomeUsd?: number; // best estimate
    householdIncomeBand?: 'lt_50k' | '50_100k' | '100_150k' | '150k_plus' | 'unknown';
    targetGradeLevels?: 'pre_k' | 'k_2' | 'k_5' | '3_5' | '6_8' | '9_12' | 'k_8' | 'k_12' | 'unknown';
    openToMultiplePrograms?: 'yes' | 'no' | 'maybe';
    internalTeachersInterested?: 'yes' | 'no' | 'unknown';
    teacherLeadsNotes?: string;
    seasonalCamps?: 'yes' | 'no' | 'maybe';
    mostPopularProgramDay?: string;
    openToAcademyOrDemo?: 'yes' | 'no' | 'maybe';
    allowsOurPaymentSystem?: 'yes' | 'no' | 'maybe';
    parentAccountsAllowed?: 'yes' | 'no' | 'maybe';
    parentEmailsAllowed?: 'yes' | 'no' | 'maybe';
    ifNoCanSchoolSendAccountCreationEmail?: 'yes' | 'no' | 'unknown';
    parentCommsNotes?: string;
    decisionProcess?: string;
    fundingSource?: string;
  };
  /**
   * Requirements + timing estimates needed before assigning a teacher.
   * Stored on the school doc under `teacherAssignRequirements.*` and referenced by ops logistics.
   */
  teacherAssignRequirements?: {
    /**
     * How many people must complete requirements (often: 3).
     */
    requiredPeopleCount?: number;
    basicBgCheck?: {
      requiredStatus?: 'yes' | 'no' | 'unknown';
      estimatedDays?: number;
      notes?: string;
    };
    schoolSpecificBgCheck?: {
      requiredStatus?: 'yes' | 'no' | 'unknown';
      runner?: 'school_runs' | 'district_portal' | 'third_party_vendor' | 'unknown';
      estimatedDays?: number;
      notes?: string;
    };
    fingerprinting?: {
      requiredStatus?: 'yes' | 'no' | 'unknown';
      runner?: 'overture' | 'school' | 'teacher' | 'unknown';
      estimatedDays?: number;
      notes?: string;
    };
    mandatoryReporting?: {
      requiredStatus?: 'yes' | 'no' | 'unknown';
      runner?: 'school' | 'district' | 'state' | 'overture' | 'unknown';
      estimatedDays?: number;
      notes?: string;
    };
    onlineTraining?: {
      requiredStatus?: 'yes' | 'no' | 'unknown';
      runner?: 'school' | 'district' | 'overture' | 'third_party' | 'unknown';
      estimatedDays?: number;
      notes?: string;
    };
  };
  /**
   * Public-school district vendor onboarding steps (school-specific).
   * Stored as a small checklist plus freeform notes.
   */
  vendorProcess?: {
    /**
     * Freeform status/notes: whether vendor info was sent, plus steps/instructions.
     */
    notes?: string;
    coiNotes?: string;
    registerForAccount?: boolean;
    requestAdditionalInsuranceCertificate?: boolean;
    uploadInsuranceCertificate?: boolean;
    uploadW9?: boolean;
    signDistrictRoomRentalAgreement?: boolean;
    signDistrictVendorAgreement?: boolean;
    submitSchoolRoomRentalRequest?: boolean;
    agreeToPayRoomRentalFee?: boolean;
  };
}

// ----------------------
// Billing / Invoicing (Admin MVP)
// ----------------------

export type BillingPaymentPreference = 'check' | 'ach' | 'card' | 'portal' | 'other';
export type BillingUserRole = 'finance' | 'programAdmin' | 'viewer';
export type BillingUserStatus = 'pending' | 'approved' | 'revoked';

export type BillingAddress = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export type BillingProfile = {
  legalName?: string;
  billingAddress?: BillingAddress;
  paymentPreference?: BillingPaymentPreference;
  netInvoiceDays?: number; // days until invoice should be sent
  netPaymentDays?: number; // days until payment is due
  invoiceTiming?: 'beginning' | 'biweekly' | 'middle_and_end' | 'end';
  /**
   * Primary billing contact. Canonical values reference `partners/{partnerId}/contacts`.
   * Historical school billing profiles may still contain school contact IDs.
   * When sending invoices, we email this contact as the main recipient and CC other authorized contacts.
   */
  billingContactId?: string;
  /**
   * Canonical partner contact IDs authorized for invoicing comms / portal access.
   * Replaces the legacy `billingUsers` email-based system.
   */
  authorizedContactIds?: string[];
  updatedAt?: Timestamp;
  updatedBy?: string;
};

export type BillingUser = {
  id: string;
  email: string;
  role: BillingUserRole;
  status: BillingUserStatus;
  validatedAt?: Timestamp;
  validatedBy?: string;
  createdAt?: Timestamp;
  createdBy?: string;
  notes?: string;
};

export type InvoiceLineItem = {
  source?: 'computed' | 'manual';
  description: string;
  rateCents: number;
  quantity: number;
  amountCents: number;
  // Optional per-squad context for school invoices (editable line items may omit this)
  squadId?: string;
  squadTitle?: string;
  paymentMode?: 'stripe' | 'school_flat_fee' | 'school_enrollment';
  serviceStartDate?: string; // YYYY-MM-DD
  serviceEndDate?: string;   // YYYY-MM-DD
  classesCount?: number;
  classDates?: string[];
  billedHeadcount?: number;
  classHoursSnapshot?: number;
  perClassRateCents?: number;
  /**
   * Manual line items are user-authored and should not be treated as squad-derived,
   * even if they include squad context for display/PDF purposes.
   */
  manual?: boolean;
  /**
   * If this manual line is an override for a squad-derived line, this records
   * which squad it replaced so selection changes can keep/remove it appropriately.
   */
  manualSourceSquadId?: string;
};

export interface InvoiceSessionClaim {
  id: string;
  partnerId: string;
  seasonId: string;
  squadId: string;
  classDate: string;
  invoiceId: string;
  state: 'draft' | 'sent';
  source: 'generated' | 'adhoc';
  billedHeadcount?: number;
  classHoursSnapshot?: number;
  perClassRateCents?: number;
  paymentMode: 'school_enrollment' | 'school_flat_fee';
  voidedAt?: Timestamp;
  voidedBy?: string;
  voidedReason?: string;
  voidedInvoiceId?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export type InvoicePdfMeta = {
  storagePath: string;
  url: string;
  filename?: string;
  version?: number;
  uploadedAt: Timestamp;
  uploadedBy: string;
  invoiceRevision?: number;
  invoiceDate?: string;
  paymentDueDate?: string;
  netPaymentDays?: number;
  renderFingerprint?: string;
};

export type InvoiceStatusStored =
  | 'draft'
  | 'ready'
  | 'sent'
  | 'acknowledged'
  | 'paid'
  | 'canceled';

export interface Invoice {
  id: string; // Firestore doc id (may be auto-generated)
  /** Historical snapshot excluded from active billing workflows. */
  legacy?: boolean;
  /** Required on newly-created invoices. */
  partnerId?: string;
  /** Required on newly-created invoices tied to a location. */
  locationId?: string;
  /** @deprecated Historical compatibility only. */
  schoolId?: string;
  // Optional grouping tag; invoices are no longer uniquely keyed by term.
  termId?: string;
  // Optional explicit coverage window (preferred long-term over termId-only).
  servicePeriodStart?: string; // YYYY-MM-DD
  servicePeriodEnd?: string;   // YYYY-MM-DD
  // Optional fast filter aid for "which squads are covered by this invoice?"
  coveredSquadIds?: string[];
  /** Optimistic-concurrency and prepared-PDF identity for material draft changes. */
  revision?: number;

  // Key dates
  invoiceDate?: string; // ISO (YYYY-MM-DD recommended)
  /** Whether invoiceDate should follow the actual send day or a human-entered override. */
  invoiceDateSource?: 'send_date' | 'manual_override';
  invoiceSendDate?: string; // ISO
  /** Original automation target, retained when an operator sends early or late. */
  plannedSendDate?: string;
  paymentDueDate?: string; // ISO
  sentTimingDays?: number;

  // Snapshots for PDF generation + audit
  schoolNameSnapshot?: string;
  schoolTermSnapshot?: string;
  serviceDescriptionSnapshot?: string;
  paymentDescriptionSnapshot?: string;
  overtureNameSnapshot?: string;
  overtureAddressSnapshot?: string;
  serviceDatesSnapshot?: { startDate?: string; endDate?: string };
  internalInvoiceNumberSnapshot?: string;
  netPaymentSnapshot?: number;
  paymentTermsSource?: 'partner_default' | 'manual_override' | 'unknown';
  paymentDueDateSource?: 'terms' | 'manual_override' | 'unknown';
  netInvoiceSnapshot?: number;
  payerLegalNameSnapshot?: string;
  payerBillingAddressSnapshot?: BillingAddress;
  paymentInstructionsSnapshot?: string;

  currency?: string;
  lineItems?: InvoiceLineItem[];
  subtotalCents?: number;
  totalCents?: number;

  status?: InvoiceStatusStored;

  pdf?: InvoicePdfMeta;
  pdfHistory?: InvoicePdfMeta[];

  sentAt?: Timestamp;
  sentBy?: string;
  sentTo?: string[];
  cc?: string[];

  // Portal acknowledgement + access tracking (first access counts as acknowledgement)
  acknowledgedAt?: Timestamp;
  acknowledgedByEmail?: string;
  portalFirstAccessAt?: Timestamp;
  portalLastAccessAt?: Timestamp;
  portalAccessCount?: number;

  paidAt?: Timestamp;
  paidBy?: string;
  paidAmountCents?: number;
  paidMethod?: 'ach' | 'check' | 'card' | 'portal' | 'other';
  paidNotes?: string;

  createdAt?: Timestamp;
  createdBy?: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
  automationKey?: string;
  generationKey?: string;
  generatedRevision?: number;
  generatedAt?: Timestamp;
  sourceDataUpdatedAt?: Timestamp;
  invoiceKind?: 'generated' | 'adhoc' | 'true_up' | 'replacement';
  operatorReminderLastSentYmd?: string;
  operatorReminderCount?: number;
  operatorReminderKind?: 'ready' | 'unsent' | 'overdue';
  remindersSnoozedUntil?: string;
  canceledAt?: Timestamp;
  canceledBy?: string;
  cancelReason?: string;
  replacesInvoiceId?: string;
  replacedByInvoiceId?: string;
}

export type GlobalDocKind = 'w9' | 'ach';

export type GlobalDoc = {
  id: GlobalDocKind;
  kind: GlobalDocKind;
  current?: InvoicePdfMeta;
  history?: InvoicePdfMeta[];
  updatedAt?: Timestamp;
  updatedBy?: string;
};

export type InvoiceTemplate = {
  id: string; // "current"
  title?: string; // e.g., "INVOICE"
  overtureName?: string;
  overtureAddress?: string;
  paymentInstructions?: string; // shown on invoice + copied block
  emailReplyTo?: string; // e.g., steven@overtureai.org
  emailIntroHtml?: string; // optional additional email body block
  logo?: {
    storagePath: string;
    url: string;
    filename?: string;
    version?: number;
    uploadedAt: Timestamp;
    uploadedBy: string;
  };
  updatedAt?: Timestamp;
  updatedBy?: string;
};

export type InvoiceAccessToken = {
  id: string; // tokenId
  invoiceId: string;
  partnerId?: string;
  locationId?: string;
  /** @deprecated Historical token compatibility only. */
  schoolId?: string;
  recipientEmail: string;
  recipientRole?: BillingUserRole;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  revokedAt?: Timestamp | null;
  revokedBy?: string | null;
  accessCount?: number;
  lastAccessedAt?: Timestamp | null;
};

export type InvoiceAccessEvent = {
  id: string;
  tokenId: string;
  invoiceId: string;
  partnerId?: string;
  locationId?: string;
  /** @deprecated Historical event compatibility only. */
  schoolId?: string;
  recipientEmail: string;
  occurredAt: Timestamp;
  userAgent?: string;
  referrer?: string;
};

export type InvoicePaymentSubmission = {
  id: string;
  invoiceId: string;
  partnerId?: string;
  locationId?: string;
  /** @deprecated Historical submission compatibility only. */
  schoolId?: string;
  tokenId: string;
  submittedByEmail: string;
  method: 'ach' | 'check' | 'card' | 'portal' | 'other';
  amountCents?: number;
  paidDate?: string; // YYYY-MM-DD
  notes?: string;
  submittedAt: Timestamp;
};

 

export type ContactRole =
  | 'primary'
  | 'principal'
  | 'afterschool_director'
  | 'teacher_champion'
  | 'marketing_director'
  | 'music_teacher'
  | 'pta_president'
  | 'operations'
  | 'finance'
  | 'facilities'
  | 'counselor'
  | 'other';

export interface SchoolContact {
  displayName: string;
  role: ContactRole;
  roleCustom?: string; // free-form role title when needed
  email?: string;
  phone?: string; // raw digits e.g. +17735551234
  linkedin?: string; // LinkedIn profile URL
  notes?: string;
  preferredChannel?: 'email' | 'phone' | 'sms';
  lastContactedAt?: number; // ms since epoch
  ownerUserId?: string; // teacher who owns the relationship
  tags?: string[];
  createdAt: number;
  updatedAt: number;
}

export type SchoolContactWithId = SchoolContact & { id: string };

// Parent-specific types
export interface ConsentRecord {
  accepted?: boolean;
  value?: 'accept' | 'deny';
  version: string;
  url: string;
  acceptedAt?: string; // ISO timestamp
  updatedAt?: string; // ISO timestamp
}

export interface ConsentEvent {
  type: 'tos' | 'photoRelease';
  version: string;
  url: string;
  value: boolean | 'accept' | 'deny';
  timestamp: string; // ISO timestamp
  ip?: string;
  userAgent?: string;
  policyHash?: string;
}

export interface ParentStudentLink {
  parentId: string;
  studentId: string;
  relationship: 'parent' | 'guardian' | 'other';
  createdAt: Timestamp | Date;
}

export interface StudentInfo {
  firstName: string;
  lastName: string;
  locationId: string;
  locationSlug?: string;
  partnerId?: string;
  legacySchoolId?: string;
  /** Historical signup value retained as read-only provenance. */
  school?: string;
}

export interface ParentSignupFormData {
  parentFirstName: string;
  parentLastName: string;
  parentEmail: string;
  parentPhone: string;
  students: StudentInfo[];
  password: string;
  tosAccepted: boolean;
  photoRelease: 'accept' | 'deny' | '';
  inviteToken?: string;
}

// Star System Types
export type StarReason =
  | 'creativity'
  | 'teamwork'
  | 'leadership'
  | 'kindness'
  | 'persistence'
  | 'focus'
  | 'positivity'
  | 'other';

export interface StarTransaction {
  id: string;
  studentId: string;
  teacherId: string;
  squadId: string;
  reason: StarReason;
  customReason?: string; // for "other" option
  timestamp: Timestamp;
  lessonDate: string; // ISO date string (YYYY-MM-DD) for grouping by lesson
  amount: number; // always 1 for now, but flexible for future
}

export interface LessonStars {
  id: string; // composite: ${studentId}_${lessonDate}_${squadId}
  studentId: string;
  squadId: string;
  lessonDate: string; // ISO date string (YYYY-MM-DD)
  starsReceived: number; // 0-3
  transactions: string[]; // array of StarTransaction IDs
  lastUpdated: Timestamp;
}

// Muse Upgrade Types
export interface MuseUpgradeTransaction {
  id: string;
  userId: string;
  museName: string; // Name of the muse being upgraded
  fromTier: MuseTier;
  toTier: MuseTier;
  starsCost: number;
  timestamp: Timestamp;
}

export interface MuseUpgradePreview {
  currentTier: MuseTier;
  nextTier: MuseTier;
  starsCost: number;
  canAfford: boolean;
  tierName: string; // "Shiny" or "Chrome"
  tierDescription: string;
}
